/*
 * SVG path generation for the Zenbul wave language.
 *
 * One idea drives the visual system: a wave that starts loud and damps to a
 * flat line. The logo mark states it as a closed ring — chop on one side,
 * clean circle on the other — and this generator states it horizontally, as
 * the trace across the process timeline and the schematic in The silencer.
 *
 * The mark itself is artwork (public/mark.svg), not generated here.
 */

/**
 * A horizontal wave whose amplitude scales from `amp` to `amp * decayTo`
 * across the width. `decayTo: 0` gives the full noise-to-silence trace.
 *
 * @param {number} W        width in viewBox units
 * @param {number} y        vertical center
 * @param {number} amp      starting amplitude
 * @param {number} freq     cycles across the width
 * @param {number} [phase=0]
 * @param {number} [decayTo=1]  amplitude multiplier at the right edge
 * @param {object} [opts]
 * @param {number} [opts.start=0]  fraction of the width where decay begins;
 *                                 amplitude is held flat before it. Use this
 *                                 when the drawing marks where attenuation
 *                                 happens — decaying from the left edge
 *                                 would contradict it.
 * @param {number} [opts.exp=1.7]  decay curve exponent.
 * @param {boolean} [opts.ease=false]
 *   false: amplitude follows v^exp. Above 1 this holds amplitude then
 *   collapses — right for a trace that decays over its whole length.
 *
 *   true: amplitude follows 1-(1-v)^exp, which drops fast and then flattens.
 *   Use this with `start`. The obvious alternative — v^exp with exp below 1 —
 *   also drops fast, but its slope at v=0 is infinite, so the curve visibly
 *   steps at the decay point and reads as a rendering glitch rather than
 *   attenuation.
 * @param {number} [opts.steps=200]
 * @returns {string} SVG path data
 */
export function wave(W, y, amp, freq, phase = 0, decayTo = 1, opts = {}) {
  const { start = 0, exp = 1.7, ease = false, steps = 200 } = opts;
  let d = '';

  for (let i = 0; i <= steps; i++) {
    const u = i / steps;
    // Remap u into the decaying region so amplitude is untouched before `start`.
    const v = start >= 1 ? 0 : Math.max(0, (u - start) / (1 - start));
    const t = ease ? 1 - Math.pow(1 - v, exp) : Math.pow(v, exp);
    const a = amp * (1 + (decayTo - 1) * t);
    const yy = y + a * Math.sin(freq * u * Math.PI * 2 + phase);
    d += (i ? 'L' : 'M') + (u * W).toFixed(2) + ' ' + yy.toFixed(2) + ' ';
  }

  return d;
}
